import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection, LOCALE_ID, importProvidersFrom, APP_INITIALIZER, provideAppInitializer } from '@angular/core';
import { initialiserInstallationPwa, initialiserMiseAJour } from '@stockflow/offline-core';
import * as Sentry from '@sentry/angular';
import { provideServiceWorker } from '@angular/service-worker';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CustomTranslateLoader } from './core/i18n/translate-loader';
import { fetchAuthSession } from 'aws-amplify/auth';
import { stockageJetons } from './core/services/stockage-jetons';
import { reprendreSession } from './core/services/reprise-session';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { verrouInterceptor } from './core/interceptors/verrou.interceptor';
import { offlineInterceptor } from './core/interceptors/offline.interceptor';
import { quotaInterceptor } from './core/interceptors/quota.interceptor';
import { webCacheInterceptor } from './core/interceptors/web-cache.interceptor';
import Lara from '@primeng/themes/lara';
import { PermissionService } from './features/security/services/permission.service';
import { AuthService } from './core/services/auth.service';
import { PlanService } from './core/services/plan.service';
import { ReferenceDataService } from './core/services/reference-data.service';
import { environment } from '../environments/environment';
import {
  POS_DATA_TOKEN, INVENTORY_DATA_TOKEN, ACCOUNTING_DATA_TOKEN,
  HR_DATA_TOKEN, PURCHASE_DATA_TOKEN,
} from './core/offline/tokens';
import { OfflinePosService } from './core/offline/services/offline-pos.service';
import { OfflineInventoryService } from './core/offline/services/offline-inventory.service';
import { OfflineAccountingService } from './core/offline/services/offline-accounting.service';
import { OfflineHrService } from './core/offline/services/offline-hr.service';
import { OfflinePurchaseService } from './core/offline/services/offline-purchase.service';
import { OfflineStateService } from './core/offline/services/offline-state.service';

registerLocaleData(localeFr);

export { CustomTranslateLoader };

export function HttpLoaderFactory(http: HttpClient) {
  return new CustomTranslateLoader(http);
}

export function initializeApp(
  permissionService: PermissionService,
  translate: TranslateService,
  authService: AuthService,
  planService: PlanService,
  refDataService: ReferenceDataService,
  offlineState: OfflineStateService,
) {
  return async () => {
    // Load translations BEFORE app renders to avoid raw keys flash
    translate.addLangs(['fr', 'en', 'es', 'ht']);
    translate.setDefaultLang('fr');
    const savedLang = localStorage.getItem('app-language') || localStorage.getItem('lang') || 'fr';
    await firstValueFrom(translate.use(savedLang));

    /**
     * LA SESSION EST RECONSTITUEE AVANT QUE LE ROUTEUR NE DECIDE.
     *
     * ── DEUX PROBLEMES DISTINCTS, ET UN SEUL ETAIT TRAITE ──
     *
     * Le premier est une course : le routeur n'attendait pas la fin de
     * l'hydratation d'IndexedDB. `roleCheckGuard` demandait la session pendant
     * qu'elle se reconstituait, recevait `UserUnAuthenticatedException`, et
     * redirigeait. Attendre ici, avant que les gardes ne tranchent, le regle —
     * et `getItem` attend deja `pret`, donc l'attente est transitive.
     *
     * Le second est que RIEN NE SE RECONSTITUAIT. Amplify lit l'access token
     * en premier et abandonne s'il manque ; or c'est precisement celui qu'on
     * refuse d'ecrire sur le disque. Le jeton de rafraichissement n'etait
     * jamais consulte : zero appel reseau, zero jeton rendu. Attendre plus
     * longtemps n'y changeait rien.
     *
     * `reprendreSession` fait l'echange qu'Amplify ne fait pas, et depose les
     * jetons frais en MEMOIRE — le disque ne porte toujours que le jeton de
     * rafraichissement. Voir `reprise-session.ts`.
     */
    /**
     * ── L AMORCAGE NE DOIT JAMAIS ATTENDRE SANS FIN ──
     *
     * Ce bloc appelait Cognito sans limite de temps, et Angular n'affiche RIEN
     * tant qu'un `APP_INITIALIZER` n'a pas resolu. Un appel reseau qui pend —
     * coupure, DNS lent, service injoignable — laissait la page tourner
     * indefiniment, SANS qu'aucune requete n'atteigne le backend.
     *
     * Symptome rapporte : « ca tourne sans cesse, je n'arrive pas a me
     * connecter ». Les journaux des six services ne montraient que les sondes
     * de sante — la preuve que rien ne partait du navigateur.
     *
     * Huit secondes suffisent largement a un echange de jeton. Passe ce delai
     * on rend la main : les gardes de route enverront vers `/login`, ce qui
     * est un ecran, pas un sablier.
     */
    const borner = <T>(p: Promise<T>, ms: number, quoi: string) =>
      Promise.race([
        p,
        new Promise<null>((r) =>
          setTimeout(() => {
            console.warn(`[Amorcage] ${quoi} n'a pas repondu en ${ms} ms.`);
            r(null);
          }, ms),
        ),
      ]);

    try {
      const reprise = await borner(
        reprendreSession(stockageJetons, environment.cognito),
        8000,
        'la reprise de session',
      );
      // LE RESULTAT SE DIT. `ResultatReprise` distingue « pas de jeton » d'un
      // « refus de Cognito » et d'un « echec reseau » — trois pannes qui se
      // ressemblent a l'ecran et se corrigent differemment. L'avaler obligeait
      // a redecouvrir laquelle a chaque fois.
      console.log('[Reprise]', reprise);
      // Sans `forceRefresh` : les jetons viennent d'etre emis. Le forcer
      // ajouterait un aller-retour a chaque demarrage, pour reobtenir ce
      // qu'on tient deja.
      await borner(fetchAuthSession(), 8000, 'fetchAuthSession');
    } catch {
      // Pas de session reconstituable — hors ligne, ou jeton revoque. Les
      // gardes trancheront, mais APRES qu'on ait essaye, et non pendant.
    }

    // Only load permissions if user is authenticated AND has a company — prevents
    // "Tenant Context missing" error for new users who haven't completed onboarding
    const isAuth = await authService.isAuthenticated();
    if (isAuth) {
      const companyId = await authService.getCompanyId();
      if (companyId) {
        /*
         * Bornees elles aussi : un service lent ne doit pas empecher
         * l'application de s'afficher. Chacune sait se recharger ensuite.
         */
        await borner(
          Promise.all([
            permissionService.loadPermissions(),
            planService.loadPlanLimits(),
            firstValueFrom(refDataService.loadGlobals()),
          ]),
          12000,
          'le chargement initial',
        );

        // Tauri desktop: init RxDB only (hydration triggered from AppComponent after render)
        if ((environment as Record<string, unknown>)['isTauri']) {
          try {
            await offlineState.initDatabase(Number(companyId));
          } catch (err) {
            console.error('[StockFlow] Offline DB init failed:', err);
          }
        }
      }
    } else {
      // No auth or no company — load globals without tenant context (for onboarding/public pages)
      firstValueFrom(refDataService.loadGlobals()).catch(() => {});
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    // `beforeinstallprompt` n'est emis qu'une fois : le service d'installation doit exister AVANT le premier ecran.
    provideAppInitializer(initialiserInstallationPwa),
    provideAppInitializer(initialiserMiseAJour),
    provideHttpClient(withInterceptors([verrouInterceptor, authInterceptor, webCacheInterceptor, offlineInterceptor, quotaInterceptor])),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'primeng'
          }
        }
      }
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'fr',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    MessageService,
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
    { provide: LOCALE_ID, useValue: 'fr' },
    { provide: DOCUMENT, useFactory: () => document },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [PermissionService, TranslateService, AuthService, PlanService, ReferenceDataService, OfflineStateService],
      multi: true
    },
    // Offline providers — only active in Tauri desktop mode
    ...((environment as any).isTauri ? [
      { provide: POS_DATA_TOKEN, useClass: OfflinePosService },
      { provide: INVENTORY_DATA_TOKEN, useClass: OfflineInventoryService },
      { provide: ACCOUNTING_DATA_TOKEN, useClass: OfflineAccountingService },
      { provide: HR_DATA_TOKEN, useClass: OfflineHrService },
      { provide: PURCHASE_DATA_TOKEN, useClass: OfflinePurchaseService },
    ] : []),
    /**
     * PWA installable : sans service worker, aucun navigateur ne propose l'installation.
     *
     * `enabled` suit l'environnement, pas `isDevMode()` : le site de dev est construit sans optimisation,
     * donc `isDevMode()` y serait vrai alors qu'on veut pouvoir y essayer l'installation. Le worker ne met
     * en cache que le COQUILLE de l'application (voir `ngsw-config.json`) — jamais une reponse `/api`.
     * `registerWhenStable` attend la fin du chargement initial, pour ne pas concurrencer les vrais appels.
     */
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorkerActif,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ]
};
